import { Locales, LocalesStrings, LocaleText } from '../types/locales'
import { createContext, ReactNode, useContext, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { getLocaleString, getLocaleValue } from '../helpers/localeHelpers'
import { LocaleStrings } from '../../MyStichtingOpenHelpers/src'
import { useRouter } from 'next/router'

const DEFAULT_FALLBACK_LOCALE: Locales = Locales.NL
const DEFAULT_FALLBACK_LOCALE_STRING: LocaleStrings = LocaleStrings.NL
const AVAILABLE_LOCALES: Locales[] = [DEFAULT_FALLBACK_LOCALE, Locales.EN]

const dayjsLocales: { [key: string]: Promise<ILocale> } = {
    [Locales.NL]: import('dayjs/locale/nl'),
    [Locales.EN]: import('dayjs/locale/en'),
}

const getDayjsLocaleTranslations = (locale: Locales): Promise<ILocale> => {
    if (!AVAILABLE_LOCALES.includes(locale)) {
        return dayjsLocales[DEFAULT_FALLBACK_LOCALE]
    }

    return dayjsLocales[locale]
}

interface LocaleProviderProps {
    children: ReactNode
}

interface LocaleProviderValue {
    activeLocale: Locales
    activeLocaleString: LocaleStrings
    defaultLocale: Locales
    getLocaleText?: (text: LocaleText, locale?: Locales) => string
    setLocale?: (locale: Locales) => void
}

export const LocaleContext = createContext<LocaleProviderValue>({
    activeLocale: DEFAULT_FALLBACK_LOCALE,
    activeLocaleString: DEFAULT_FALLBACK_LOCALE_STRING,
    defaultLocale: DEFAULT_FALLBACK_LOCALE,
})

export const useLocale = (): LocaleProviderValue => useContext(LocaleContext)

export const LocaleProvider = ({ children }: LocaleProviderProps) => {
    const router = useRouter()
    const { locale, pathname, query, asPath } = router
    const [activeLocale, setActiveLocale] = useState<Locales>(getLocaleValue(locale as LocalesStrings))

    useEffect(() => {
        getDayjsLocaleTranslations(activeLocale).then(() => {
            dayjs.locale(activeLocale === Locales.NL ? 'nl' : 'en')
        })
        // Pushes the current route but with the new locale
        router.push({ pathname, query }, asPath, { locale: getLocaleString(activeLocale) })
    }, [activeLocale])

    const getLocaleText = (text: LocaleText, locale?: Locales): string => {
        if (locale && text[getLocaleString(locale)]) return text[getLocaleString(locale)]
        if (text[getLocaleString(activeLocale)]) return text[getLocaleString(activeLocale)]
        if (text[getLocaleString(DEFAULT_FALLBACK_LOCALE)]) return text[getLocaleString(DEFAULT_FALLBACK_LOCALE)]
        return text[Object.keys(text)[0]]
    }

    const setLocale = (locale: Locales): void => {
        if (locale !== activeLocale) setActiveLocale(locale)
    }

    const getActiveLocalesString = (): LocaleStrings => {
        return activeLocale === Locales.EN ? LocaleStrings.EN : LocaleStrings.NL
    }

    return (
        <LocaleContext.Provider
            value={{
                activeLocale,
                activeLocaleString: getActiveLocalesString(),
                defaultLocale: DEFAULT_FALLBACK_LOCALE,
                getLocaleText,
                setLocale,
            }}
        >
            {children}
        </LocaleContext.Provider>
    )
}
